"use client";

import { useAuth } from "@/components/AuthContext";
import { BrandLockup } from "@/components/brand";
import { LandingFooter } from "@/components/landing";
import Link from "@/components/school/SchoolLink";
import { trackTutorEvent } from "@/lib/analytics/tutor-events";
import { isSchoolPath } from "@/lib/school-routes";
import {
  ArrowRight,
  BarChart3,
  BookOpenCheck,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Mic2,
  Play,
  Quote,
  Sparkles,
  UsersRound,
} from "lucide-react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useRef } from "react";

const tutorBenefits = [
  {
    icon: BookOpenCheck,
    title: "Start with what you already teach",
    text: "Set the language, level, grammar and scenario for a Classroom. ChickyTutor follows your teaching focus.",
  },
  {
    icon: Mic2,
    title: "Give students the missing repetitions",
    text: "Learners speak privately, receive a focused correction, repeat the improved sentence and continue the conversation.",
  },
  {
    icon: ClipboardCheck,
    title: "Keep practice connected to the next lesson",
    text: "Tutor Studio shows Classroom participation today. The founding pilot adds concise next-lesson briefings from assigned practice.",
  },
];

const pilotSteps = [
  "Create one Classroom for an existing student or group.",
  "Share one direct invitation and let students practise between lessons.",
  "Use the resulting activity to prepare the next human session.",
];

const videoQuartiles = [25, 50, 75] as const;

export function TutorsLandingPage() {
  const school = isSchoolPath(usePathname());
  const { user, isLoading: authLoading } = useAuth();
  const posthog = usePostHog();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const trackedQuartilesRef = useRef(new Set<number>());
  const hasTrackedStartRef = useRef(false);
  const hasTrackedCompletionRef = useRef(false);
  const hasTrackedLandingViewRef = useRef(false);

  const primaryHref = useMemo(
    () => (user ? "/tutor-studio?source=tutors_page" : "/tutoronboarding?source=tutors_page"),
    [user]
  );

  useEffect(() => {
    if (authLoading || hasTrackedLandingViewRef.current) return;
    hasTrackedLandingViewRef.current = true;
    trackTutorEvent(posthog, "tutor_landing_viewed", {
      surface: "tutors_page",
      is_authenticated: Boolean(user),
    });
  }, [authLoading, posthog, user]);

  const trackCta = (placement: string) => {
    trackTutorEvent(posthog, "tutor_cta_clicked", {
      surface: "tutors_page",
      placement,
      destination: user ? "/tutor-studio" : "/tutoronboarding",
      is_authenticated: Boolean(user),
    });
  };

  const playVideo = async () => {
    const video = videoRef.current;
    if (!video) return;

    video.scrollIntoView({ behavior: "smooth", block: "center" });
    trackTutorEvent(posthog, "tutor_cta_clicked", {
      surface: "tutors_page",
      placement: "hero_video",
      destination: "video",
    });

    try {
      await video.play();
    } catch {
      // Browser media policies may still require the user to use native controls.
    }
  };

  const handleVideoPlay = () => {
    if (hasTrackedStartRef.current) return;
    hasTrackedStartRef.current = true;
    trackTutorEvent(posthog, "tutor_video_started", {
      surface: "tutors_page",
      video_id: "french_hybrid_course_launch",
    });
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;

    const progress = (video.currentTime / video.duration) * 100;
    videoQuartiles.forEach((quartile) => {
      if (progress >= quartile && !trackedQuartilesRef.current.has(quartile)) {
        trackedQuartilesRef.current.add(quartile);
        trackTutorEvent(posthog, "tutor_video_progressed", {
          surface: "tutors_page",
          video_id: "french_hybrid_course_launch",
          quartile,
        });
      }
    });
  };

  const handleVideoEnded = () => {
    if (hasTrackedCompletionRef.current) return;
    hasTrackedCompletionRef.current = true;
    trackTutorEvent(posthog, "tutor_video_completed", {
      surface: "tutors_page",
      video_id: "french_hybrid_course_launch",
    });
  };

  return (
    <main className="min-h-screen bg-[#f5f3ed] text-[#171716]">
      <header
        className={`${school ? "relative" : "fixed inset-x-0 top-0"} z-50 border-b border-white/10 bg-[#0f0f14]/95 backdrop-blur-xl`}
      >
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link href="/" className="flex items-center gap-3">
            {school ? (
              <BrandLockup surface="dark" className="h-10 w-auto" />
            ) : (
              <>
                <Image
                  src="/ui/Chickylogovector.gif"
                  alt="ChickyTutor"
                  width={40}
                  height={40}
                  className="h-10 w-10 rounded-full"
                />
                <span className="text-lg font-extrabold text-white sm:text-xl">ChickyTutor</span>
              </>
            )}
            <span className="hidden rounded-md border border-orange-300/20 bg-orange-500/10 px-2 py-1 text-xs font-bold text-orange-200 sm:inline-flex">
              For tutors
            </span>
          </Link>

          <div className="hidden items-center gap-7 text-sm font-semibold text-white/70 lg:flex">
            <a href="#how-it-works" className="transition hover:text-white">
              How it works
            </a>
            <a href="#tutor-view" className="transition hover:text-white">
              Tutor view
            </a>
            <a href="#pilot" className="transition hover:text-white">
              Pilot
            </a>
            {!school && (
              <>
                <Link href="/schools" className="transition hover:text-white">
                  For schools
                </Link>
                <Link href="/teach" className="transition hover:text-white">
                  Teach with us
                </Link>
              </>
            )}
          </div>

          <Link
            href={primaryHref}
            onClick={() => trackCta("header")}
            className="inline-flex min-h-10 items-center justify-center rounded-lg bg-orange-500 px-4 text-sm font-extrabold text-white transition hover:bg-orange-400"
          >
            {user ? "Open Tutor Studio" : "Start with my students"}
          </Link>
        </nav>
      </header>

      <section className="relative overflow-hidden bg-[#0f0f14] pt-16 text-white">
        <div className="absolute inset-0 opacity-70 [background:radial-gradient(circle_at_78%_18%,rgba(253,149,79,0.20),transparent_34%),radial-gradient(circle_at_20%_75%,rgba(255,255,255,0.06),transparent_28%)]" />
        <div className="relative mx-auto grid min-h-[calc(94vh-4rem)] max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-md border border-orange-300/20 bg-orange-500/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-200">
              <Sparkles className="h-4 w-4" />
              Your teaching, extended
            </div>
            <h1 className="mt-7 text-5xl font-black leading-[0.94] tracking-[-0.045em] sm:text-6xl lg:text-7xl">
              Give every student a speaking practice tutor
            </h1>
            <p className="mt-7 max-w-xl text-lg font-medium leading-8 text-white/72 sm:text-xl">
              Turn what you teach into guided AI speaking practice. Students return more confident,
              with practice connected to your learning focus.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link
                href={primaryHref}
                onClick={() => trackCta("hero")}
                className="inline-flex min-h-14 items-center justify-center rounded-lg bg-orange-500 px-7 text-base font-extrabold text-white shadow-[0_18px_60px_rgba(253,149,79,0.24)] transition hover:bg-orange-400"
              >
                Add ChickyTutor to my lessons
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <button
                type="button"
                onClick={playVideo}
                className="inline-flex min-h-14 items-center justify-center rounded-lg border border-white/15 bg-white/[0.05] px-7 text-base font-bold text-white transition hover:bg-white/[0.1]"
              >
                <Play className="mr-2 h-5 w-5 fill-current" />
                Watch the 69-second example
              </button>
            </div>
            <p className="mt-5 text-sm font-semibold text-white/45">
              Built from a real partnership with L&apos;École de français.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-5 rounded-[28px] bg-orange-400/10 blur-3xl" />
            <div className="relative overflow-hidden rounded-2xl border border-white/12 bg-black shadow-[0_34px_100px_rgba(0,0,0,0.5)]">
              <video
                ref={videoRef}
                className="aspect-video w-full bg-black object-cover"
                controls
                playsInline
                preload="metadata"
                poster="/ui/french-hybrid-course-launch-16x9-poster.jpg"
                onPlay={handleVideoPlay}
                onTimeUpdate={handleVideoTimeUpdate}
                onEnded={handleVideoEnded}
              >
                <source src="/ui/french-hybrid-course-launch-16x9.mp4" type="video/mp4" />
                Your browser does not support HTML video.
              </video>
              <div className="grid gap-px border-t border-white/10 bg-white/10 sm:grid-cols-3">
                {["Human teaching", "Daily private practice", "A stronger next lesson"].map(
                  (label, index) => (
                    <div
                      key={label}
                      className="bg-[#15151a] px-4 py-3 text-sm font-bold text-white/70"
                    >
                      <span className="mr-2 text-orange-300">0{index + 1}</span>
                      {label}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-600">
              The missing practice
            </p>
            <h2 className="mt-4 text-4xl font-black tracking-[-0.035em] sm:text-5xl">
              One lesson cannot provide every speaking repetition.
            </h2>
            <p className="mt-6 text-lg leading-8 text-black/60">
              Your students may understand the material and still freeze when they need to speak.
              ChickyTutor gives them a private correction-and-repeat loop without asking you to add
              daily live hours.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {tutorBenefits.map((item, index) => (
              <article
                key={item.title}
                className="rounded-xl border border-black/10 bg-white p-6 shadow-sm"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-orange-100 text-orange-700">
                  <item.icon className="h-5 w-5" />
                </div>
                <div className="mt-8 text-xs font-black uppercase tracking-[0.18em] text-black/35">
                  Step {index + 1}
                </div>
                <h3 className="mt-3 text-xl font-black">{item.title}</h3>
                <p className="mt-3 leading-7 text-black/58">{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="tutor-view" className="bg-white py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="rounded-2xl border border-black/10 bg-[#111116] p-6 text-white shadow-[0_30px_90px_rgba(15,15,20,0.18)] sm:p-8">
            <div className="flex items-center justify-between border-b border-white/10 pb-5">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-300">
                  Founding pilot preview
                </p>
                <h3 className="mt-2 text-2xl font-black">French A2 · Introductions</h3>
              </div>
              <BarChart3 className="h-7 w-7 text-white/35" />
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {[
                ["6 of 8", "practised"],
                ["42 min", "spoken"],
                ["4", "need attention"],
              ].map(([value, label]) => (
                <div key={label} className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
                  <div className="text-2xl font-black text-orange-300">{value}</div>
                  <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-white/45">
                    {label}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-3">
              <div className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
                <div className="text-xs font-black uppercase tracking-wide text-white/40">
                  Common gap
                </div>
                <p className="mt-2 font-semibold text-white/85">
                  Four learners still omit the article before a language name.
                </p>
              </div>
              <div className="rounded-lg border border-orange-300/20 bg-orange-500/10 p-4">
                <div className="text-xs font-black uppercase tracking-wide text-orange-200">
                  Suggested warm-up
                </div>
                <p className="mt-2 font-semibold text-white/90">
                  Two-minute paired introductions using “Ça fait… que”.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-600">
              Built for the next human lesson
            </p>
            <h2 className="mt-4 text-4xl font-black tracking-[-0.035em] sm:text-5xl">
              See teaching signals, not another wall of transcripts.
            </h2>
            <p className="mt-6 text-lg leading-8 text-black/60">
              Tutor Studio already shows who practised and when. The next founding-pilot phase turns
              assigned practice into common learning patterns and suggested warm-ups, while you stay
              in control of the method and the relationship.
            </p>
            <div className="mt-8 space-y-4">
              {[
                "Your language, level and lesson focus shape the practice.",
                "Students receive private correction without classroom pressure.",
                "You decide what becomes part of the next live lesson.",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 font-semibold text-black/72">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#efeae0] py-24">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
          <Quote className="mx-auto h-10 w-10 text-orange-600" />
          <blockquote className="mt-7 text-3xl font-black leading-tight tracking-[-0.03em] sm:text-4xl">
            “It does not replace the teacher. It extends the learning after class.”
          </blockquote>
          <p className="mt-5 text-base font-bold text-black/55">
            The model demonstrated with L&apos;École de français and ChickyTutor
          </p>
        </div>
      </section>

      <section id="pilot" className="bg-[#0f0f14] py-24 text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_0.85fr] lg:px-8">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-300">
              Founding tutor pilot
            </p>
            <h2 className="mt-4 max-w-3xl text-4xl font-black tracking-[-0.035em] sm:text-5xl">
              Start with the students you already teach.
            </h2>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-white/62">
              Create one Classroom, invite a small group and test how connected practice changes the
              next lesson. No course migration or new scheduling system required.
            </p>
            <Link
              href={primaryHref}
              onClick={() => trackCta("pilot")}
              className="mt-9 inline-flex min-h-14 items-center justify-center rounded-lg bg-orange-500 px-7 text-base font-extrabold text-white transition hover:bg-orange-400"
            >
              {user ? "Open Tutor Studio" : "Create my first Classroom"}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
            <div className="flex items-center gap-3">
              <UsersRound className="h-7 w-7 text-orange-300" />
              <h3 className="text-xl font-black">A practical first pilot</h3>
            </div>
            <div className="mt-6 space-y-5">
              {pilotSteps.map((step, index) => (
                <div key={step} className="flex gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-orange-500 text-sm font-black text-white">
                    {index + 1}
                  </div>
                  <p className="pt-1 font-semibold leading-6 text-white/72">{step}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-6 text-sm font-semibold text-white/45">
              <Clock3 className="h-5 w-5" />
              Designed around your existing lesson rhythm.
            </div>
          </div>
        </div>
      </section>

      {!school && <LandingFooter />}
    </main>
  );
}
